/**
 * Pre-rendered demo questions for the home QuestionDemo component.
 *
 * Decision 12.5 (ADR-005 §7): pre-rendered, not live, at launch.
 * UX rule (ADR-006 §5.1): at least one question must be the "honest-empty" state
 * — a question SICA cannot answer, to demonstrate the no-source policy.
 *
 * Citations are illustrative until the backend wires real document fragments.
 * TODO(12.5): replace with real fragments from the IAC once the backend exposes them.
 */

// The Citation type lives in @sica/ui because the four-part visual contract
// (CitationBlock) belongs to the shared visual system. Imported and re-exported
// here so existing `@/data/demo-questions` consumers continue to compile.
import type { Citation } from '@sica/ui';
export type { Citation };

export type DemoQuestion = {
  id: string;
  prompt: string;
  kind: 'answered' | 'no-source';
  answer?: string;
  citation?: Citation;
};

export const DEMO_QUESTIONS: readonly DemoQuestion[] = [
  {
    id: 'matematica-2do-ciclo',
    prompt: '¿Qué dice el NAP de Matemática para 2.º ciclo sobre numeración y operaciones?',
    kind: 'answered',
    answer:
      'El NAP de Matemática para el 2.º ciclo de Educación Primaria establece, entre los aprendizajes prioritarios, el reconocimiento y uso de números naturales, sus relaciones de orden, y las operaciones básicas, en situaciones problemáticas que requieran:',
    citation: {
      excerpt:
        'Reconocer y usar los números naturales, las operaciones de suma, resta, multiplicación y división, y las relaciones de orden, explicando las estrategias utilizadas y comparándolas con las de otros.',
      document: 'NAP — Matemática · 2.º Ciclo de la Educación Primaria',
      page: 17,
      jurisdiction: 'Nacional',
    },
  },
  {
    id: 'lengua-caba-1er-ciclo',
    prompt: '¿Cómo desarrolla CABA los NAP de Prácticas del Lenguaje para 1.er ciclo?',
    kind: 'answered',
    answer:
      'El Diseño Curricular de la Ciudad Autónoma de Buenos Aires desarrolla los NAP de Prácticas del Lenguaje para 1.er ciclo organizando los contenidos en cuatro ámbitos: leer, escribir, hablar y escuchar, en situaciones contextualizadas:',
    citation: {
      excerpt:
        'En 1.er ciclo se prioriza la formación de los niños como hablantes, lectores y escritores, en el marco de prácticas sociales del lenguaje que los involucren con propósitos comunicativos genuinos.',
      document: 'Diseño Curricular de la CABA — Educación Primaria · Prácticas del Lenguaje',
      page: 42,
      jurisdiction: 'CABA',
    },
  },
  {
    id: 'cs-naturales-7mo',
    prompt: 'Indicadores de progreso para Ciencias Naturales en 7.º grado del NAP.',
    kind: 'answered',
    answer:
      'Los indicadores de progreso en Ciencias Naturales para el final del 2.º ciclo / inicio del 3.er ciclo (7.º grado en jurisdicciones que lo incluyen en el primario) priorizan la construcción de explicaciones causales sobre fenómenos:',
    citation: {
      excerpt:
        'La identificación de relaciones de interdependencia entre los seres vivos y el ambiente, y el uso de modelos sencillos para explicar fenómenos del mundo natural, comunicando los resultados con vocabulario científico.',
      document: 'NAP — Ciencias Naturales · 2.º Ciclo',
      page: 24,
      jurisdiction: 'Nacional',
    },
  },
  {
    id: 'no-source-example',
    prompt: '¿Qué piensa el ministerio sobre la inteligencia artificial generativa en el aula?',
    kind: 'no-source',
  },
];
